import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { useAuth } from '../AuthContext';
import type { AdminSurfaceConfig } from '../surface';

interface LoginPageProps {
  surfaceConfig: AdminSurfaceConfig;
}

interface LocationState {
  from?: string;
}

export default function LoginPage({ surfaceConfig }: LoginPageProps) {
  const { status, configError, signIn } = useAuth();
  const location = useLocation();
  const state = location.state as LocationState | null;
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    document.title = `${surfaceConfig.loginTitle} | Octopus`;
  }, [surfaceConfig.loginTitle]);

  if (status === 'authenticated') {
    return <Navigate to={state?.from ?? '/'} replace />;
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage(null);
    setIsSubmitting(true);

    try {
      await signIn(identifier, password);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : '로그인에 실패했습니다.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="login-page">
      <section className="login-panel" aria-labelledby="login-title">
        <div className="login-panel__brand">
          <span className="brand-mark brand-mark--large">
            <ShieldCheck size={28} />
          </span>
          <span className="surface-badge">{surfaceConfig.badge}</span>
        </div>
        <h1 id="login-title">{surfaceConfig.loginTitle}</h1>
        <p>{surfaceConfig.loginDescription}</p>

        {configError ? <div className="alert alert--error">{configError}</div> : null}
        {errorMessage ? <div className="alert alert--error">{errorMessage}</div> : null}

        <form className="login-form" onSubmit={handleSubmit}>
          <label>
            <span>{surfaceConfig.usernameLabel}</span>
            <input
              type="text"
              value={identifier}
              placeholder={surfaceConfig.usernamePlaceholder}
              autoComplete="username"
              onChange={(event) => setIdentifier(event.target.value)}
              disabled={status === 'config_error' || isSubmitting}
            />
          </label>
          <label>
            <span>비밀번호</span>
            <input
              type="password"
              value={password}
              placeholder="비밀번호"
              autoComplete="current-password"
              onChange={(event) => setPassword(event.target.value)}
              disabled={status === 'config_error' || isSubmitting}
            />
          </label>
          <button
            className="primary-button"
            type="submit"
            disabled={status === 'config_error' || isSubmitting || !identifier.trim() || !password}
          >
            {isSubmitting ? '확인 중' : '로그인'}
          </button>
        </form>
      </section>
    </main>
  );
}
